import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import useFiltersFetchedData from '../../../../helperHooks/useFiltersFetchedData';
import { CATEGORIES_API_CACHE_KEY } from '../../../../constants';
import { API_V1_HOSTNAME_KEY } from '../../../../localStorageKeys';
import { CategoryForList, CategoryFromAPI, CategoryToCreate } from '../helpers/types';
import { ApiContext } from '../../../../context/ApiContext';

const useCategories = () => {
  const { t } = useTranslation();
  const { apiFetch } = useContext(ApiContext);
  const queryClient = useQueryClient();
  const [categoriesAddedSuccessMessage, setCategoriesAddedSuccessMessage] = useState('');
  const [categories, setCategories] = useState<CategoryForList[]>([]);

  const { data: categoryRawData, isLoading: isLoadingCategories } = useFiltersFetchedData<{
    Data: CategoryFromAPI[];
    Metadata: {
      Page: number;
      Limit: number;
      TotalPages: number;
      TotalRecords: number;
      CurrentPageSize: number;
    };
  }>(
    CATEGORIES_API_CACHE_KEY,
    `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/categories`,
    'categories'
  );

  const {
    isLoading: isAddingCategories,
    mutate: addCategories,
    isError: errorAddingCategories,
  } = useMutation(
    (newCategories: CategoryToCreate[]) =>
      apiFetch(
        `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/categories`,
        newCategories,
        'POST'
      ),
    {
      onSuccess(_response) {
        queryClient.invalidateQueries(['categories']);
        setCategoriesAddedSuccessMessage(t('screens.products.sidekick.import.categoriesAdded'));
      },
    }
  );

  // TODO: Revisit this to see if we want to remove duplicates

  useEffect(() => {
    if (categoryRawData && categoryRawData?.Data?.length > 0) {
      const categoriesData = categoryRawData.Data;
      const categoriesMapped =
        categoriesData?.map((category) => ({
          value: category.Id.toString(),
          label: category.Name,
        })) || [];

      const categoriesLabels = categoriesMapped.map(({ label }) => label);
      const noDuplicateCategories = categoriesMapped.filter(
        ({ label }, index) => !categoriesLabels.includes(label, index + 1)
      );
      setCategories([{ value: '-1', label: 'Top Level' }].concat(noDuplicateCategories));
    }
  }, [categoryRawData]);

//   const categoriesData: Category[] = useMemo(
//     () => (categoryRawData && categoryRawData?.Data?.length > 0 ? categoryRawData?.Data : []),
//     [categoryRawData]
//   );

  return {
    categories,
    isLoadingCategories,
    isAddingCategories,
    addCategories,
    errorAddingCategories,
    categoriesAddedSuccessMessage,
    setCategoriesAddedSuccessMessage,
  };
};

export default useCategories;
